import { getUserSession } from '../../../shared/authentication/helpers/sessionStorage'
import { BRAND_TYPES } from '../config/brandSetupConfig'

const cleanValue = (value) => `${value || ''}`.trim()

const mapSteward = (steward = {}) => {
  const normalizedSteward = steward || {}

  const mappedSteward = {
    name: cleanValue(normalizedSteward.name || normalizedSteward.display_name),
    login_id: cleanValue(
      normalizedSteward.login_id || normalizedSteward.loginId,
    ),
    email: cleanValue(normalizedSteward.email || normalizedSteward.mail),
  }

  return mappedSteward.name && mappedSteward.login_id && mappedSteward.email
    ? mappedSteward
    : null
}

const mapStewardList = (stewards = []) =>
  (stewards || []).filter(Boolean).map(mapSteward).filter(Boolean)

const areStewardsEqual = (left = null, right = null) =>
  Boolean(
    left &&
    right &&
    left.name === right.name &&
    left.login_id === right.login_id &&
    left.email === right.email,
  )

const mapBrandId = (brandId) => {
  const normalizedBrandId = cleanValue(brandId)
  const numericBrandId = Number(normalizedBrandId)

  return normalizedBrandId && Number.isSafeInteger(numericBrandId)
    ? numericBrandId
    : normalizedBrandId
}

const resolveOriginalBrandId = (brand = {}) =>
  brand.brand_id || brand.brandId || brand.externalBrandId || brand.id

export const createBrandSlug = (name = '') =>
  name
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')

export const mapCreateBrandPayload = (
  form = {},
  session = getUserSession(),
) => {
  const primarySteward = mapSteward(form.primarySteward)

  return {
    brand_name: cleanValue(form.selectedBrand?.brand_name || form.brandName),
    brand_type: cleanValue(form.brandType),
    brand_id: mapBrandId(form.selectedBrand?.brand_id),
    slug: cleanValue(form.slug),
    brand_category_id: cleanValue(form.category?.id),
    reason: cleanValue(form.reason?.value),
    ...(primarySteward ? { primary_steward: primarySteward } : {}),
    supporting_stewards: (form.supportingStewards || [])
      .filter(Boolean)
      .map(mapSteward)
      .filter(Boolean),
    created_by_enterprise_user_id: cleanValue(session.lanId),
    created_by_display_name: cleanValue(
      session.displayName || session.fullName,
    ),
    created_by_email: cleanValue(session.email),
  }
}

export const mapUpdateBrandPayload = (form = {}, originalBrand = {}) => {
  const brandType = cleanValue(form.brandType || originalBrand.brandType)
  const isMasterBrand = brandType === BRAND_TYPES.MASTER

  const payload = {}

  const nextPrimarySteward = mapSteward(form.primarySteward)
  const currentPrimarySteward = mapSteward(originalBrand.primarySteward)
  if (
    nextPrimarySteward &&
    !areStewardsEqual(nextPrimarySteward, currentPrimarySteward)
  ) {
    payload.primary_steward = nextPrimarySteward
  }

  const nextSupportingStewards = mapStewardList(form.supportingStewards)
  const currentSupportingStewards = mapStewardList(
    originalBrand.supportingStewards,
  )
  if (
    nextSupportingStewards.length > 0 ||
    currentSupportingStewards.length > 0
  ) {
    payload.supporting_stewards = nextSupportingStewards
  }

  if (isMasterBrand) {
    return payload
  }

  const nextBrandName = cleanValue(
    form.selectedBrand?.brand_name || form.brandName,
  )
  const currentBrandName = cleanValue(
    originalBrand.name || originalBrand.brandName || originalBrand.brand_name,
  )
  if (nextBrandName !== currentBrandName) {
    payload.brand_name = nextBrandName
  }

  const nextBrandId = mapBrandId(form.selectedBrand?.brand_id)
  const currentBrandId = mapBrandId(resolveOriginalBrandId(originalBrand))
  if (`${nextBrandId || ''}` !== `${currentBrandId || ''}`) {
    payload.brand_id = nextBrandId
  }

  const nextSlug = cleanValue(form.slug)
  const currentSlug = cleanValue(originalBrand.slug || originalBrand.brandSlug)
  if (nextSlug !== currentSlug) {
    payload.slug = nextSlug
  }

  const nextCategoryId = cleanValue(form.category?.id)
  const currentCategoryId = cleanValue(
    originalBrand.category?.id ||
      originalBrand.categoryId ||
      originalBrand.category_id,
  )
  if (nextCategoryId !== currentCategoryId) {
    payload.brand_category_id = nextCategoryId
  }

  return payload
}
